import { useMemo, useState } from "react"
import { Line, LineChart, Legend, ReferenceLine, Tooltip, XAxis, YAxis, ResponsiveContainer } from "recharts"
import { Text } from "@/components"
import { CapTableFunding, MonthlyHiresAndDepartures } from "@/types"
import { Box, HStack } from "@chakra-ui/react"
import { ChartRangeSelector } from "./ChartRangeSelector"
import { DateRange, DATE_RANGE_OPTIONS, filterBeforeFoundingDate, filterByDateRange } from "./chartUtils"
import { useFundraisingMarkers } from "./FundraisingMarkers"

interface Props {
  data: MonthlyHiresAndDepartures[]
  capTableFunding?: CapTableFunding[]
  foundingDate?: string | null
}

function linearRegression(values: (number | undefined)[]) {
  const points: { x: number; y: number }[] = []
  values.forEach((v, i) => {
    if (v !== undefined) points.push({ x: i, y: v })
  })
  if (points.length < 2) return values.map(() => undefined)

  const n = points.length
  const sumX = points.reduce((s, p) => s + p.x, 0)
  const sumY = points.reduce((s, p) => s + p.y, 0)
  const sumXY = points.reduce((s, p) => s + p.x * p.y, 0)
  const sumXX = points.reduce((s, p) => s + p.x * p.x, 0)

  const slope = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX)
  const intercept = (sumY - slope * sumX) / n

  return values.map((_, i) => Math.round((slope * i + intercept) * 100) / 100)
}

export const HiresAndDeparturesChart = ({ data, capTableFunding = [], foundingDate }: Props) => {
  const [dateRange, setDateRange] = useState<DateRange>("all")

  const chartData = useMemo(() => {
    const sorted = [...data]
      .map((d) => ({
        date: d.date,
        hires: d.hiresPct * 100,
        departures: d.departuresPct * 100,
      }))
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())

    const filtered = filterByDateRange(filterBeforeFoundingDate(sorted, foundingDate), dateRange)

    const hiresTrend = linearRegression(filtered.map((d) => d.hires))
    const departuresTrend = linearRegression(filtered.map((d) => d.departures))

    return filtered.map((d, i) => ({
      ...d,
      hiresTrend: hiresTrend[i],
      departuresTrend: departuresTrend[i],
    }))
  }, [data, dateRange, foundingDate])

  const fundraisingMarkers = useFundraisingMarkers(capTableFunding, chartData.map((d) => d.date))

  if (chartData.length === 0) {
    return <Text color="text.muted">No hires and departures data available.</Text>
  }

  return (
    <Box w="100%" p={5} border="1px" borderColor="gray.200" borderRadius={10} boxShadow="sm" bg="card.bg">
      <HStack mb={4} justifyContent="space-between" alignItems="center">
        <Text fontSize="lg" fontWeight="bold">
          Hires and Departures
        </Text>
        <ChartRangeSelector options={DATE_RANGE_OPTIONS} value={dateRange} onChange={setDateRange} />
      </HStack>
      <ResponsiveContainer width="100%" height={350}>
        <LineChart data={chartData}>
          <XAxis dataKey="date" fontSize={12} />
          <YAxis fontSize={12} unit="%" />
          <Tooltip contentStyle={{ backgroundColor: "var(--chakra-colors-card-bg)" }} />
          <Legend />
          {fundraisingMarkers.map((m, i) => (
            <ReferenceLine key={`fr-${i}`} x={m.x} stroke="#A0AEC0" strokeDasharray="4 4" label={{ value: m.label, position: "top", fontSize: 10, fill: "#718096" }} />
          ))}
          <Line type="monotone" dataKey="hires" name="Hires %" stroke="#38A169" dot={false} connectNulls />
          <Line type="monotone" dataKey="departures" name="Departures %" stroke="#E53E3E" dot={false} connectNulls />
          <Line type="monotone" dataKey="hiresTrend" name="Hires Trend" stroke="#38A169" strokeDasharray="5 5" dot={false} connectNulls />
          <Line type="monotone" dataKey="departuresTrend" name="Departures Trend" stroke="#E53E3E" strokeDasharray="5 5" dot={false} connectNulls />
        </LineChart>
      </ResponsiveContainer>
    </Box>
  )
}
